'use client'

import React, { ReactNode, useEffect, useMemo } from 'react'

import { usePathname } from 'next/navigation'
import Link from 'next/link'
import { useLocale } from 'next-intl'
import { useReferrerContext } from '@/context/referrerContext'
import { localizedHref } from '@/utils/localizedHref'

const excludePaths = ['companies']

/**
 * Maps breadcrumb path segments to their target URLs
 * @param pathSegment - The path segment to map
 * @param locale - The current locale
 * @returns The mapped URL or null if no mapping exists
 */
const getBreadcrumbHref = (pathSegment: string, locale: string): string | null => {
  const pathMappings: Record<string, string> = {
    products: localizedHref('/portfolio', locale),
    solutions: localizedHref('/portfolio', locale),
    resources: localizedHref('/knowledge-hub', locale),
    article: localizedHref('/knowledge-hub', locale),
  }

  return pathMappings[pathSegment] || null
}

export interface BreadCrumbProps {
  separator?: React.ReactNode
  activeClasses?: string
  containerClasses?: string
  listClasses?: string
  capitalizeLinks?: boolean
  areaSlugs?: string[]
  title?: string | null
}

const Breadcrumbs = ({
  separator,
  containerClasses,
  listClasses,
  activeClasses,
  capitalizeLinks,
  areaSlugs,
  title,
}: BreadCrumbProps) => {
  const paths = usePathname()
  const locale = useLocale()
  const {
    isFromWikiPage,
    isFromProductGroup,
    previousUrl,
    originalAreasUrl,
    lastSegmentFromAreas,
  } = useReferrerContext()

  // Analyze current path to determine page type
  const { isProductGroup, isProductDetail, isSolution } = useMemo(() => {
    const segments = paths?.split('/').filter(Boolean) || []
    const productsIndex = segments.findIndex((segment) => segment === 'products')
    const solutionsIndex = segments.findIndex((segment) => segment === 'solutions')

    return {
      // Is a product group page (like /products/group-name)
      isProductGroup: productsIndex >= 0 && segments.length === productsIndex + 2,
      // Is a product detail page (like /products/group-name/product-name)
      isProductDetail: productsIndex >= 0 && segments.length > productsIndex + 2,
      // Is a solution page
      isSolution: solutionsIndex >= 0,
    }
  }, [paths])

  // Debugging logs
  useEffect(() => {
    if (paths) {
      console.log('Current path:', paths)
      console.log('Is product group page:', isProductGroup)
      console.log('Is product detail page:', isProductDetail)
      console.log('Is solution page:', isSolution)
      console.log('Is from wiki page:', isFromWikiPage)
      console.log('Is from product group:', isFromProductGroup)
      console.log('Previous URL:', previousUrl)
      console.log('Original areas URL:', originalAreasUrl)
      console.log('Last segment from areas:', lastSegmentFromAreas)
    }
  }, [
    paths,
    isProductGroup,
    isProductDetail,
    isSolution,
    isFromWikiPage,
    isFromProductGroup,
    previousUrl,
    originalAreasUrl,
    lastSegmentFromAreas,
  ])

  if (!paths) return null

  const pathNames = paths.split('/').filter((path) => path)
  // localePrefix 'as-needed': the default locale is served unprefixed, so only
  // strip the first segment when it is an explicit locale prefix (e.g. /de/...).
  const filteredPathNames = pathNames[0] === locale ? pathNames.slice(1) : pathNames

  // Check if we're on a workflows path
  const isOnWorkflowsPath = filteredPathNames.includes('workflows')
  const workflowsIndex = isOnWorkflowsPath ? filteredPathNames.indexOf('workflows') : -1

  // Check if we're on a wiki/deep workflow page (has segments after the workflow)
  // Workflows path structure: workflows/[domainSlug]/[workflowSlug]/[...stage or wiki pages]
  // (technology guides hang off the domain: workflows/[domainSlug]/technology/[slug])
  // Workflow hub page has 3 segments starting at workflows: workflows, domainSlug, workflowSlug
  // Deeper pages have 4+ segments
  const isWikiPage = isOnWorkflowsPath && filteredPathNames.length > workflowsIndex + 3

  // Determine if we should show the workflows breadcrumb
  // Show workflows breadcrumb if we're on a product/solution page and have originalAreasUrl
  // OR if we're on a workflows page itself
  // Note: we use originalAreasUrl instead of lastSegmentFromAreas because the latter
  // has a fallback to previousUrl which can be stale for one render cycle.
  const shouldShowWorkflowsBreadcrumb = Boolean(
    (originalAreasUrl && (isProductGroup || isProductDetail || isSolution)) || isOnWorkflowsPath,
  )

  // Determine root breadcrumb: "Workflows" if coming from a workflow, otherwise "Products & Solutions"
  let rootBreadcrumb: { label: string; href: string } | null = null
  if (shouldShowWorkflowsBreadcrumb) {
    rootBreadcrumb = {
      label: 'Workflows',
      href: localizedHref('/workflows', locale),
    }
  } else if (isProductGroup || isProductDetail || isSolution) {
    rootBreadcrumb = {
      label: 'Products & Solutions',
      href: localizedHref('/portfolio', locale),
    }
  }

  const formatText = (text: string) => {
    return text
      .replace(/[-_]/g, ' ')
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }

  return (
    <>
      <ul className={containerClasses}>
        {/* Add workflows breadcrumb first if needed */}
        {rootBreadcrumb && (
          <>
            <li
              className={`${listClasses} text-primary-800`}
              title={`Coming from: ${rootBreadcrumb.href}`}
            >
              <Link href={rootBreadcrumb.href}>
                {capitalizeLinks ? formatText(rootBreadcrumb.label) : rootBreadcrumb.label}
              </Link>
            </li>
            {separator}
          </>
        )}

        {/* Show source wiki page when navigating from a workflow to products/solutions */}
        {shouldShowWorkflowsBreadcrumb &&
          lastSegmentFromAreas &&
          !isOnWorkflowsPath &&
          originalAreasUrl && (
            <>
              <li className={`${listClasses} text-primary-800`}>
                {/* localizedHref heals referrer URLs still in a historical scheme */}
                <Link href={localizedHref(originalAreasUrl, locale)}>
                  {capitalizeLinks ? formatText(lastSegmentFromAreas) : lastSegmentFromAreas}
                </Link>
              </li>
              {separator}
            </>
          )}

        {filteredPathNames.map((link, index) => {
          // Skip excluded paths completely
          if (excludePaths.includes(link)) {
            return null
          }

          // Skip the "workflows" literal segment — the root breadcrumb covers it
          if (link === 'workflows') {
            return null
          }

          // Skip "products"/"solutions" segments when rootBreadcrumb already covers them
          if (rootBreadcrumb && (link === 'products' || link === 'solutions')) {
            return null
          }

          // If we're on a wiki/deep workflow page and this is the domain slug
          // (segment right after "workflows"), show "..."
          const isAreaSlug = isWikiPage && workflowsIndex >= 0 && index === workflowsIndex + 1
          const displayText = isAreaSlug ? '...' : link

          const defaultHref = localizedHref(
            `/${filteredPathNames.slice(0, index + 1).join('/')}`,
            locale,
          )

          // Check if this path segment has a special mapping
          const mappedHref = getBreadcrumbHref(link, locale)
          if (mappedHref) {
            return (
              <React.Fragment key={index}>
                <li className={paths === mappedHref ? `${listClasses} ${activeClasses}` : listClasses}>
                  {isAreaSlug ? (
                    displayText
                  ) : (
                    <Link href={mappedHref}>
                      {capitalizeLinks ? formatText(displayText) : displayText}
                    </Link>
                  )}
                </li>
                {filteredPathNames.length !== index + 1 && separator}
              </React.Fragment>
            )
          }

          const href = defaultHref

          // Skip rendering non-clickable crumbs
          if (areaSlugs?.includes(link)) {
            return null
          }

          return (
            <React.Fragment key={index}>
              <li className={paths === href ? `${listClasses} ${activeClasses}` : listClasses}>
                {index === filteredPathNames.length - 1 ? (
                  title || (capitalizeLinks && !isAreaSlug ? formatText(displayText) : displayText)
                ) : isAreaSlug ? (
                  displayText
                ) : (
                  <Link href={href}>{capitalizeLinks ? formatText(displayText) : displayText}</Link>
                )}
              </li>
              {filteredPathNames.length !== index + 1 && separator}
            </React.Fragment>
          )
        })}
      </ul>
    </>
  )
}

export default Breadcrumbs
